import { createAuthClient } from 'better-auth/client'
import { emailOTPClient } from 'better-auth/client/plugins'
// Subpath import: origin.ts is a pure module (no DOM lib deps), so this
// package's nodenext typecheck doesn't have to compile api-client's index.
import { deriveApiOrigin } from '@papan/api-client/origin'

let clientInstance: ReturnType<typeof buildAuthClient> | null = null

function buildAuthClient() {
  return createAuthClient({
    baseURL: getBaseURL(),
    // Match the server-side plugin list — emailOTPClient exposes
    // `signIn.emailOtp({ email })` (send code) and
    // `signIn.emailOtp({ email, otp })` (verify + sign in).
    plugins: [emailOTPClient()],
  })
}

function getBaseURL() {
  const isBrowser = typeof globalThis !== 'undefined' && 'location' in globalThis
  if (!isBrowser) return process.env.BETTER_AUTH_URL || 'http://localhost:3001'
  // API base resolution (#234, single code path for all envs):
  // VITE_API_URL build-time override → runtime hostname derivation
  // (@papan/api-client origin.ts) → location.origin (same-origin dev).
  // import.meta.env is vite-injected; cast to satisfy tsc
  // (this file is bundled into the web SPA by vite at build time).
  const apiUrl = (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL) || ''
  if (apiUrl) return apiUrl.replace(/\/$/, '')
  const loc = (globalThis as unknown as { location: { origin: string; hostname: string } }).location
  return deriveApiOrigin(loc.hostname) || loc.origin
}

export function getAuthClient() {
  if (!clientInstance) {
    clientInstance = buildAuthClient()
  }
  return clientInstance
}

/**
 * Helper to call Better Auth API endpoints directly (for flows not exposed on the client).
 */
/**
 * `code` and `status` are carried alongside `message` so callers can map a
 * server error to human copy without string-matching. Better Auth returns a
 * stable `code` (`INVALID_PASSWORD`, …) and its `message` is developer-facing —
 * surfacing it verbatim is how "user already has a password" ended up in front
 * of a user trying to change theirs (#568). Additive: existing callers read
 * `message` and are unaffected.
 */
async function authFetch<T = unknown>(path: string, body: Record<string, unknown>): Promise<{ data?: T; error?: { message: string; code?: string; status?: number } }> {
  try {
    const res = await fetch(`${getBaseURL()}/api/auth${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    })
    // DEFENSIVE PARSE, not `await res.json()`. `sensitiveSessionMiddleware`
    // throws `APIError("UNAUTHORIZED")` with NO BODY, so `res.json()` threw
    // `Unexpected end of JSON input` — which escaped to the catch below, arrived
    // with no `status` and no `code`, and was rendered to the user verbatim. The
    // branch written to catch exactly that 401 could never fire, because the
    // status was destroyed one layer down.
    const text = await res.text()
    let json: Record<string, unknown> = {}
    let parsed = true
    try { json = text ? JSON.parse(text) as Record<string, unknown> : {} } catch { parsed = false }

    // TOLERANT ONLY ON THE ERROR BRANCH. An unparseable body there is still an
    // error — the status is what matters and it must survive. On the SUCCESS
    // branch it is the opposite: reporting `{ data: {} }` for a body we could
    // not read turns a request the server never processed into a reported
    // success, and the caller tells the user it worked.
    //
    // That is not hypothetical here. `getBaseURL()` falls back to
    // `location.origin` for a host missing from `STABLE_HOST_TO_API`, Cloudflare
    // Pages answers `/api/*` with the SPA SHELL — 200, text/html — and CLAUDE.md
    // records that exact chain for `app.gnexis.ai`. Swallowed, it renders
    // "Password changed. Your other devices have been signed out." to someone
    // whose password did not change and whose sessions were never revoked.
    // No `text &&` here: the ternary short-circuits on an empty body, so
    // `JSON.parse` never runs and `parsed` stays true. The extra term read like
    // a guard and asserted nothing — dropping it changed no test.
    if (res.ok && !parsed) {
      return { error: { message: 'The server returned an unexpected response.', status: res.status } }
    }
    if (!res.ok) {
      return {
        error: {
          message: (json.message as string) || (json.error as string) || `Request failed (${res.status})`,
          code: typeof json.code === 'string' ? json.code : undefined,
          status: res.status,
        },
      }
    }
    return { data: json as T }
  } catch (err) {
    return { error: { message: err instanceof Error ? err.message : 'Request failed' } }
  }
}

export const authClient = {
  get signIn() { return getAuthClient().signIn },
  get signUp() { return getAuthClient().signUp },
  get signOut() { return getAuthClient().signOut },
  get useSession() { return getAuthClient().useSession },
  get getSession() { return getAuthClient().getSession },

  /**
   * Request a password reset email.
   *
   * `/request-password-reset`, NOT `/forget-password`. Better Auth renamed the
   * route and 1.4.18 does not serve the old one — it 404s. The whole
   * forgot-password flow was therefore dead: `ForgotPasswordForm` deliberately
   * shows "check your email" regardless of the result (enumeration defence, and
   * correct), so the user waited for a message that was never generated. #567
   * cites this flow as THE workaround for its lockout, and the Settings password
   * form links to it as its escape hatch — both were pointing at a 404.
   *
   * Found by trying to use it. `password-reset-route.test.ts` now pins the path
   * against the routes better-auth actually exposes, so the next rename fails a
   * test instead of a user.
   */
  forgetPassword: {
    email: (params: { email: string; redirectTo: string }) =>
      authFetch('/request-password-reset', params),
  },

  /** Update mutable profile fields on the signed-in user. Better Auth's
   * /update-user endpoint accepts a partial body — only the fields you
   * pass change. Returns the updated user shape. Caller is responsible
   * for triggering a session refresh so the cached avatar/taskbar
   * components pick up the new value. */
  updateUser: (params: { name?: string; image?: string | null }) =>
    authFetch<{ user: { id: string; name: string; email: string } }>('/update-user', params),

  /** Reset password with a token from the email */
  resetPassword: (params: { newPassword: string; token: string }) =>
    authFetch('/reset-password', params),

  /** Resend a verification email */
  sendVerificationEmail: (params: { email: string; callbackURL: string }) =>
    authFetch('/send-verification-email', params),

  /** Verify email with a token from the email. Better-auth's verify-email
   * endpoint is GET-only — POSTs return 404. We deliberately don't set
   * `Accept: application/json`: with JSON accept, better-auth returns a
   * JSON success body but does NOT set the auto-sign-in cookie. With the
   * default text/html accept, it returns 302 + Set-Cookie. We use
   * `redirect: 'manual'` so the fetch resolves on the redirect (without
   * following cross-origin), and the browser still applies Set-Cookie
   * to the SPA's cookie partition because the request originated here. */
  verifyEmail: async (params: { token: string }): Promise<{ data?: unknown; error?: { message: string } }> => {
    try {
      const url = `${getBaseURL()}/api/auth/verify-email?token=${encodeURIComponent(params.token)}`
      const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        redirect: 'manual',
      })
      // type === 'opaqueredirect' means the redirect was hit (good).
      // type === 'basic' with res.ok also means success (server didn't redirect).
      if (res.type === 'opaqueredirect' || res.ok) {
        return { data: { success: true } }
      }
      return { error: { message: `Verify failed (${res.status})` } }
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Verify failed' } }
    }
  },

  /** Set password on an OAuth-only account (no current password needed) */
  setPassword: (params: { newPassword: string }) =>
    authFetch('/set-password', params),

  /** Change password (requires current password) */
  /**
   * `revokeOtherSessions` is declared here because the route accepts it
   * (`update-user.mjs`'s zod body) and `authFetch` JSON-stringifies whatever it
   * is handed. Declaring it is what stops a caller needing a cast — and stops
   * a later refactor that destructures this shim from dropping the flag
   * silently, which would leave an intruder's session live while the UI said
   * otherwise.
   */
  changePassword: (params: {
    currentPassword: string
    newPassword: string
    revokeOtherSessions?: boolean
  }) => authFetch('/change-password', params),
}
